"use client";

import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import {
  SkuTransaction,
} from "@chipi-stack/types";
import { useChipiContext } from "../context";

type GetSkuTransactionInput = {
  id: string;
  getBearerToken?: () => Promise<string | null | undefined>;
  queryOptions?: Omit<UseQueryOptions<SkuTransaction, Error>, 'queryKey' | 'queryFn'>;
};

export function useGetSkuTransaction(input?: GetSkuTransactionInput) {
  const { chipiSDK } = useChipiContext();
  const queryClient = useQueryClient();
  
  const query = useQuery<SkuTransaction, Error>({
    queryKey: ["sku-transaction", input?.id],
    queryFn: async () => {
      if (!input?.id) throw new Error("id is required");
      if (!input?.getBearerToken) throw new Error("getBearerToken is required");
      const bearerToken = await input.getBearerToken();
      if (!bearerToken) throw new Error("Bearer token is required");
      return chipiSDK.skuTransactions.getSkuTransaction(input.id, bearerToken);
    },
    enabled: Boolean(input?.id && input?.getBearerToken),
    ...input?.queryOptions
  });

  const fetchSkuTransaction = async (newInput: GetSkuTransactionInput) => {
    return queryClient.fetchQuery({
      queryKey: ["sku-transaction", newInput?.id],
      queryFn: async () => {
        if (!newInput?.id) throw new Error("id is required");
        if (!newInput?.getBearerToken) throw new Error("getBearerToken is required");
        const bearerToken = await newInput.getBearerToken();
        if (!bearerToken) throw new Error("Bearer token is required");
        return chipiSDK.skuTransactions.getSkuTransaction(newInput.id, bearerToken);
      },
    });
  };

  return {
    ...query,
    fetchSkuTransaction,
  };
}
